// BaseNode.js

import { useState } from 'react';
import { Handle } from 'reactflow';

export const BaseNode = ({
  id,
  data,
  type,
  title,
  handles = [],
  fields = [],
  style = {},
  children
}) => {
  // Initialize field states using a single state object
  const [fieldValues, setFieldValues] = useState(() => {
    const initialValues = {};
    fields.forEach(field => {
      initialValues[field.name] = data?.[field.name] || field.defaultValue || '';
    });
    return initialValues;
  });

  const handleFieldChange = (fieldName, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const renderField = (field) => {
    const value = fieldValues[field.name];

    switch (field.type) {
      case 'text':
      case 'input':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 text-sm bg-pipeline-bg border border-pipeline-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pipeline-accent placeholder-gray-500 transition-shadow ${field.className || ''}`}
            style={field.style}
          />
        );

      case 'select':
        return (
          <select value={value} onChange={(e) => handleFieldChange(field.name, e.target.value)} className={`w-full px-3 py-2 text-sm bg-pipeline-bg border border-pipeline-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pipeline-accent transition-shadow max-w-full ${field.className || ''}`} style={field.style}>
            {field.options?.map(option => (
              <option key={option.value} value={option.value} className="bg-pipeline-bg text-white">
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 text-sm bg-pipeline-bg border border-pipeline-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pipeline-accent placeholder-gray-500 resize-none overflow-y-auto transition-shadow ${field.className || ''}`}
            style={{
              ...field.style,
              minHeight: '60px',
              maxHeight: '120px'
            }}
            rows={field.rows || 3}
          />
        );

      default:
        return null;
    }
  };

  const renderHandles = () => {
    return handles.map((handle, index) => (
      <Handle
        key={handle.id || `${id}-${handle.type}-${index}`}
        type={handle.type}
        position={handle.position}
        id={handle.id || `${id}-${handle.type}-${index}`}
        style={handle.style}
      />
    ));
  };

  return (
    <div
      className={`
        bg-pipeline-node border border-pipeline-border rounded-card
        min-w-[220px] shadow-2xl transition-all duration-300
        hover:border-pipeline-accent/30 group/node relative
        ${style.className || ''}
      `}
      style={style}
    >
      <div className="
        px-4 py-3 border-b border-pipeline-border 
        flex items-center justify-between
        bg-white/[0.02] rounded-t-card
      ">
        <span className="text-[11px] font-bold uppercase tracking-widest text-pipeline-muted group-hover/node:text-pipeline-accent transition-colors">
          {title}
        </span>
        <div className="w-1.5 h-1.5 rounded-full bg-pipeline-accent/40 group-hover/node:bg-pipeline-accent group-hover/node:shadow-[0_0_8px_#7000FF] transition-all" />
      </div>

      <div className="p-4 flex flex-col gap-4 overflow-y-auto max-h-[300px]">
        {children || (
          fields.map(field => (
            <div key={field.name} className="flex flex-col gap-1.5">
              {field.label && (
                <label className="text-[10px] font-bold uppercase tracking-wider text-pipeline-muted ml-0.5">
                  {field.label}
                </label>
              )}
              {renderField(field)}
            </div>
          ))
        )}
      </div>

      {renderHandles()}
    </div>
  );
};
